import React from "react";
import { useSelector } from "react-redux";
import { FaCheck, FaChevronUp, FaChevronDown } from "react-icons/fa";
import assignment from "../../assets/Thread/Assignment.jpg";
import {
  formatYYYY_MM_DD_HH_MM_A,
} from "../../utils/dateFormating";
import { currencyFormatter } from "../../utils/formatting";
import { FaArrowRightLong } from "react-icons/fa6";
import { orderStatus } from "../../constants/commonConstants";
import { canTipWriter } from "../../utils/writerTipEligibility";

const OrderDetails = ({
  tutorial,
  isExpanded,
  toggleSection,
  handleViewOrder,
  handleAcceptOrder,
  handleMakeExtraPayment,
  showDeliveryActions = true,
  children,
}) => {
  const thread = useSelector((state) => state.thread);
  const selectedOrderStatus = thread?.selectedOrder?.status;
  const isTipAvailable = canTipWriter(thread?.selectedOrder);
  const isExtraPaymentAvailable = [
    orderStatus.PENDING_WRITER,
    orderStatus.ORDER_IN_PROGRESS,
    orderStatus.IN_REVISION,
  ].includes(selectedOrderStatus);
  const isTipSummaryVisible = [
    orderStatus.ORDER_DELIVERED,
    orderStatus.IN_REVISION,
    orderStatus.COMPLETED,
    orderStatus.COMPLAINT_RAISED,
    orderStatus.RESOLVED,
  ].includes(selectedOrderStatus);
  const totalTipAmount = (thread?.selectedOrder?.tipDetails || []).reduce(
    (sum, tip) => {
      if (tip?.paid === false) return sum;
      return sum + (parseFloat(tip?.amount || 0) || 0);
    },
    0
  );

  return (
    <div className="mt-[22px]">
      <div className="flex items-start justify-between ">
        <h1 className="ml-3 sm:ml-[20px] font-roboto font-semibold text-[18px] leading-5 text-primary mb-4">
          Order Details
        </h1>
        <div
          className="cursor-pointer mr-3 sm:mr-[22px]"
          onClick={toggleSection}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <FaChevronUp className="w-[20px] text-secondary" />
          ) : (
            <FaChevronDown className="w-[20px] text-secondary" />
          )}
        </div>
      </div>

      <div className="flex flex-row space-x-2 font-inter font-medium text-level-5 leading-3 text-secondary">
        <div className="ml-3 sm:ml-[20px]">
          <img
            src={assignment}
            alt="Assignment"
            className="w-[68px] h-[48px] rounded-[8px] border border-medium-gray"
          />
        </div>
        <div>
          <h1 className="font-inter text-secondary mt-[4px]  font-medium text-level-5">
            {thread?.selectedOrder?.orderRequirements?.subject}
          </h1>
          <button
            className="font-inter mt-[12px] font-medium text-level-5 leading-3 underline text-blue-shades"
            onClick={handleViewOrder} // Pass tutorial ID
          >
            View Requirements
          </button>
        </div>
      </div>

      {/* Expandable Section */}
      {isExpanded && (
        <div>
          <div className="mt-4 font-inter text-[12px] leading-3 font-medium text-secondary mx-3 sm:mx-[20px] space-y-2">
            <div className="flex justify-between">
              <span>Order ID</span>
              <span className="text-gray">{tutorial.roomId}</span>
            </div>
            <div className="flex justify-between">
              <span>Price</span>
              <span className="text-gray">{currencyFormatter.format(tutorial.price || 0)  || "$0.00"}</span>
            </div>
            <div className="flex justify-between">
              <span>Order Date</span>
              <span className="text-gray">
                {formatYYYY_MM_DD_HH_MM_A(tutorial?.createdAt)}
                {/* {tutorial.date}, {tutorial.time} */}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Delivery Date</span>
              <span className="text-gray">
                {formatYYYY_MM_DD_HH_MM_A(tutorial?.deadline)}
                {/* {tutorial.ddate}, {tutorial.dtime} */}
              </span>
            </div>
          </div>

          <div className="mx-2 sm:mx-[14px] my-[24px] bg-[#E6E7EC] h-[1px] rounded-full"></div>
        </div>
      )}

      {children}

      {showDeliveryActions && selectedOrderStatus === orderStatus.ORDER_DELIVERED && (
        <div className="mx-3 sm:ml-[20px] sm:mr-0 mt-[16px]">
          <button
            className="flex h-[40px] w-full max-w-[240px] items-center justify-center gap-[12px] rounded-[5px] bg-[#0F2E8A] px-[30px] py-[10px] font-roboto text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-[#1D4ED8]"
            onClick={handleAcceptOrder}
          >
            <span className="flex items-center justify-center whitespace-nowrap">
              Accept Order
            </span>
            <FaCheck className="h-4 w-4 text-white" />
          </button>
        </div>
      )}

      {(isExtraPaymentAvailable || (showDeliveryActions && isTipAvailable)) && (
          <div className="mx-3 sm:ml-[20px] sm:mr-0 mt-[16px]">
            <button
              className={`flex items-center justify-center w-full max-w-[240px] h-[40px] rounded-[5px] font-roboto text-[13px] text-white font-semibold gap-[12px] px-[30px] py-[10px] transition-all duration-200 ${isTipAvailable ? "bg-[#9a5b00] hover:bg-[#7a4700]" : "bg-secondary"}`}
              onClick={handleMakeExtraPayment}
            >
              <span className="flex justify-center items-center whitespace-nowrap">
                {isTipAvailable ? "Tip Writer" : "Make Extra Payment"}
              </span>
              <FaArrowRightLong className="w-4 h-4 text-white" />
            </button>
          </div>
        )}

      {isTipSummaryVisible && (
        <div className="mx-3 sm:mx-[20px] mt-[12px] flex max-w-[240px] items-center justify-between rounded-[6px] border border-[#BFDBFE] bg-blue-50 px-3 py-2 font-inter">
          <span className="text-[12px] font-semibold leading-4 text-secondary">
            Total Tip
          </span>
          <span className="text-[12px] font-semibold leading-4 text-blue-shades">
            {currencyFormatter.format(totalTipAmount || 0)}
          </span>
        </div>
      )}

      <div className="mx-2 sm:mx-[14px] my-[24px] bg-[#E6E7EC] h-[1px] rounded-full"></div>
    </div>
  );
};

export default OrderDetails;
